import {
  Intent,
  MessageSender,
  getEntityLabel,
  getMessageSenderLabel,
} from 'components/MyTaskArea/taskDetailConstants';

import { MY_TASK_STATUS } from 'global/constants';

import { getCanonicalFieldName } from 'utils/FieldNameMapping';

const fieldKey = (f) => `${f.entityId}:${getCanonicalFieldName(f.field)}`;

export const getFileDisplayName = (file) => {
  if (!file?.filename) {
    return '';
  }
  const { filename, extension } = file;
  if (extension && !filename.endsWith(`.${extension}`)) {
    return `${filename}.${extension}`;
  }
  return filename;
};

export const getDocumentUploadFieldName = (fileId) => {
  const normalizedId = String(fileId ?? '').trim();
  if (!normalizedId) {
    throw new Error('getDocumentUploadFieldName requires a non-empty file id');
  }
  return `document_${normalizedId}`;
};

const mapMessageField = (field) => ({
  id: field.id,
  intent: field.intent,
  entity: field.entity,
  entityId: field.entityId,
  field: field.field,
  value: field.value,
  entityLabel: getEntityLabel(field.entity),
});

const mapMessageFile = (file) => ({
  id: file.id,
  intent: file.intent,
  documentType: file.documentType,
  file: file.file,
});

const mapMessageAttachment = (attachment) => ({
  id: attachment.id,
  file: attachment.file,
  displayName: getFileDisplayName(attachment.file),
  downloadUrl: attachment.file?.downloadUrl ?? null,
});

const mapMessage = (message) => ({
  id: message.id,
  timestamp: message.timestamp,
  message: message.message,
  sender: message.sender,
  senderLabel: getMessageSenderLabel(message.sender),
  fields: (message.fields ?? []).map(mapMessageField),
  files: (message.files ?? []).map(mapMessageFile),
  attachments: (message.attachments ?? []).map(mapMessageAttachment),
});

export const mapInstallerAntragDetail = (raw) => {
  if (!raw) {
    return null;
  }

  const sortedMessages = [...(raw.messages ?? [])].sort(
    (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
  );

  const antragFields = raw.fields ?? [];
  const antragFiles = raw.files ?? [];
  const hasAntragLevelData = antragFields.length > 0 || antragFiles.length > 0;

  const providedDocumentTypes = hasAntragLevelData
    ? new Set(antragFiles.filter((f) => f.intent === Intent.PROVIDED).map((f) => f.documentType))
    : new Set(
        sortedMessages.flatMap((msg) =>
          (msg.files ?? []).filter((f) => f.intent === Intent.PROVIDED).map((f) => f.documentType)
        )
      );

  // Only consider PROVIDED fields from messages after the last CLERK message,
  // so that a CLERK re-request always overrides earlier PROVIDED values.
  let lastClerkIdx = -1;
  for (let i = 0; i < sortedMessages.length; i += 1) {
    if (sortedMessages[i].sender === MessageSender.CLERK) lastClerkIdx = i;
  }
  const providedFieldKeys = new Set(
    sortedMessages
      .filter((_, i) => i > lastClerkIdx)
      .flatMap((msg) =>
        (msg.fields ?? []).filter((f) => f.intent === Intent.PROVIDED).map(fieldKey)
      )
  );

  const outstandingFieldsByKey = new Map();
  const outstandingFilesByTypeId = new Map();

  const clerkMessages = sortedMessages.filter((m) => m.sender === MessageSender.CLERK);
  const lastClerkMsg = clerkMessages[clerkMessages.length - 1];
  for (const msg of clerkMessages) {
    for (const field of msg.fields ?? []) {
      if (field.intent === Intent.REQUESTED && !providedFieldKeys.has(fieldKey(field))) {
        outstandingFieldsByKey.set(fieldKey(field), mapMessageField(field));
      }
    }
    for (const file of msg.files ?? []) {
      if (file.intent === Intent.REQUESTED && !providedDocumentTypes.has(file.documentType)) {
        outstandingFilesByTypeId.set(file.documentType, mapMessageFile(file));
      }
    }
  }

  const hasOpenRequests = outstandingFieldsByKey.size > 0 || outstandingFilesByTypeId.size > 0;
  const hasTextRequest = Boolean(lastClerkMsg?.message?.trim());

  const aggregatedLastMessage =
    (hasOpenRequests || hasTextRequest) && lastClerkMsg
      ? {
          ...mapMessage(lastClerkMsg),
          fields: Array.from(outstandingFieldsByKey.values()),
          files: Array.from(outstandingFilesByTypeId.values()),
        }
      : null;

  // Show older clerk messages in history; the latest one is represented by lastMessage.

  return {
    id: raw.id,
    name: raw.antragtyp,
    status: MY_TASK_STATUS[raw.antragstatus] ?? String(raw.antragstatus),
    createdAt: raw.eingangsdatum,
    antragsubtyp: raw.antragsubtyp,
    antragstatus: raw.antragstatus,
    lastMessage: aggregatedLastMessage,
    messages: sortedMessages.filter((msg) => msg.id !== lastClerkMsg?.id).map(mapMessage),
  };
};
